import { createSelector } from 'reselect'
import {
  type PassportCardQuoteParams,
  type PassportCardQuoteRequestParams,
  travellerDetailsFromInsuranceTravellers,
  travellerDetailsFromOccupants,
} from 'lib/insurance/passportCardQuoteUtils'
import { getNoProtectionPassportCardInsuranceItem } from '../view/insurance'
import { getCheckoutInsuranceDates, getPassportCardCheckoutCountries } from './insurance'
import { getCheckoutInsuranceTravellers } from './getCheckoutInsuranceTravellers'
import getCheckoutQuoteSessionId from './getCheckoutQuoteSessionId'
import getPassportCardInsuranceCoverAmountFromCart from './getPassportCardInsuranceCoverAmountFromCart'
import getPassportCardQuoteItems from '../view/itemViews/getPassportCardQuoteItems'
import getEligibleInsuranceLoyaltyTier from 'checkout/selectors/loyalty/getEligibleInsuranceLoyaltyTier'
import { PASSPORT_CARD_SELLING_POINT_ONLINE_TOUR_CRUISE, PRODUCT_ID_PASSPORT_CARD } from 'constants/insurance'
import { isStandaloneToursCruisesCart } from 'checkout/lib/insurance/passportCardToursCruises'
import { isStandaloneToursCruisesOrder } from 'lib/order/orderPassportCardInsuranceUtils'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { getTotalPriceFromExistingOrder } from './getCheckoutInsuranceCoverAmount'

export type PassportCardQuoteInsuranceItemInput = Pick<App.Checkout.InsuranceItem,
  'coverageAmount' |
  'countries' |
  'endDate' |
  'excessAmount' |
  'passportCardOptionalCovers' |
  'passportCardTravellers' |
  'startDate'
>

export interface PassportCardCheckoutQuoteRequestParamsProps {
  params?: PassportCardQuoteParams
  insuranceItem?: PassportCardQuoteInsuranceItemInput
  checkoutDates?: {
    startDate?: string
    endDate?: string
  }
  checkoutCountries?: Array<string>
}

interface BuildPassportCardCheckoutQuoteRequestParams {
  params?: PassportCardQuoteParams
  insuranceItem?: PassportCardQuoteInsuranceItemInput
  checkoutDates: {
    startDate?: string
    endDate?: string
  }
  checkoutCountries: Array<string>
  checkoutTravellers: App.Occupants
  passportCardInsuranceCoverAmountFromCart: number | undefined
  passportCardQuoteItems: Array<App.PassportCardQuoteItem>
  currentRegionCode: string
  currentCurrency: string
  sessionId: string
  loyaltyTier?: string
  spoofingAgentId?: string
  sellingPoint?: string
}

export function buildPassportCardCheckoutQuoteRequestParams({
  params,
  insuranceItem,
  checkoutDates,
  checkoutCountries,
  checkoutTravellers,
  passportCardInsuranceCoverAmountFromCart,
  passportCardQuoteItems,
  currentRegionCode,
  currentCurrency,
  sessionId,
  loyaltyTier,
  spoofingAgentId,
  sellingPoint,
}: BuildPassportCardCheckoutQuoteRequestParams): PassportCardQuoteRequestParams | undefined {
  if (!passportCardInsuranceCoverAmountFromCart || passportCardInsuranceCoverAmountFromCart <= 0) return undefined
  if (!params && !insuranceItem) return undefined

  const quoteParams = params ?? {
    travellersDetails: insuranceItem?.passportCardTravellers?.length
      ? travellerDetailsFromInsuranceTravellers(insuranceItem.passportCardTravellers)
      : travellerDetailsFromOccupants(checkoutTravellers),
    destinationCountries: insuranceItem?.countries ?? (checkoutCountries.length ? checkoutCountries : []),
    tripStartDate: insuranceItem?.startDate ?? checkoutDates.startDate ?? '',
    tripEndDate: insuranceItem?.endDate ?? checkoutDates.endDate ?? '',
    productId: PRODUCT_ID_PASSPORT_CARD,
  }
  const hasCancellation = insuranceItem?.coverageAmount !== undefined
  const hasExcess = insuranceItem?.excessAmount !== undefined
  const filters = hasCancellation || hasExcess ? {
    cancellation_limit: hasCancellation ? insuranceItem?.coverageAmount : undefined,
    excess_limit: hasExcess ? insuranceItem?.excessAmount : undefined,
  } : undefined

  return {
    ...quoteParams,
    totalPrice: passportCardInsuranceCoverAmountFromCart,
    countryCode: currentRegionCode,
    currency: currentCurrency,
    sessionId,
    items: passportCardQuoteItems,
    filters,
    optionalCovers: insuranceItem?.passportCardOptionalCovers,
    loyaltyTier,
    spoofingAgentId,
    sellingPoint,
  }
}

const getPassportCardCheckoutQuoteRequestParams = createSelector(
  (_state: App.State, props?: PassportCardCheckoutQuoteRequestParamsProps) => props?.params,
  (state: App.State, props?: PassportCardCheckoutQuoteRequestParamsProps) =>
    props ? props.insuranceItem : getNoProtectionPassportCardInsuranceItem(state),
  (state: App.State, props?: PassportCardCheckoutQuoteRequestParamsProps) =>
    props?.checkoutDates ?? getCheckoutInsuranceDates(state),
  (state: App.State, props?: PassportCardCheckoutQuoteRequestParamsProps) =>
    props?.checkoutCountries ?? getPassportCardCheckoutCountries(state),
  (state: App.State) => getCheckoutInsuranceTravellers(state),
  (state: App.State) => getPassportCardInsuranceCoverAmountFromCart(state),
  (state: App.State) => getPassportCardQuoteItems(state),
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => getCheckoutQuoteSessionId(state),
  (state: App.State) => getEligibleInsuranceLoyaltyTier(state),
  (state: App.State) => state.auth.account.agentId,
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => getTotalPriceFromExistingOrder(state),
  (
    params,
    insuranceItem,
    checkoutDates,
    checkoutCountries,
    checkoutTravellers,
    passportCardInsuranceCoverAmountFromCart,
    passportCardQuoteItems,
    currentRegionCode,
    currentCurrency,
    sessionId,
    loyaltyTier,
    spoofingAgentId,
    cartItems,
    postPurchase,
    existingOrder,
    existingOrderCoverAmount,
  ): PassportCardQuoteRequestParams | undefined => {
    // A post-purchase insurance-only cart has no trip totals of its own: the existing order
    // is what the policy covers, the same source XCover quotes against. Other post-purchase
    // modes keep pricing the cart, whose items carry the new booking totals.
    const isPostPurchaseInsurance = postPurchase === 'insurance'
    const coverAmount = passportCardInsuranceCoverAmountFromCart ||
      (isPostPurchaseInsurance ? existingOrderCoverAmount : undefined)
    const isOnlineToursCruisesTraffic = isStandaloneToursCruisesCart(cartItems) ||
      (isPostPurchaseInsurance && !!existingOrder && isStandaloneToursCruisesOrder(existingOrder))

    return buildPassportCardCheckoutQuoteRequestParams({
      params,
      insuranceItem,
      checkoutDates,
      checkoutCountries,
      checkoutTravellers,
      passportCardInsuranceCoverAmountFromCart: coverAmount,
      passportCardQuoteItems,
      currentRegionCode,
      currentCurrency,
      sessionId,
      loyaltyTier,
      spoofingAgentId,
      // Online (non-agent) tour/cruise traffic gets the ONLINE_TOUR_CRUISE selling
      // point, whether the cover is bought with the booking or added to it afterwards.
      // Agent traffic keeps the server-derived agent/lp default.
      sellingPoint: !spoofingAgentId && isOnlineToursCruisesTraffic
        ? PASSPORT_CARD_SELLING_POINT_ONLINE_TOUR_CRUISE
        : undefined,
    })
  },
)

export default getPassportCardCheckoutQuoteRequestParams
